import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2, Trash2, Users, User } from 'lucide-react';
import type { ApiError, SopAssignmentTarget } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { Tabs } from '@/components/ui/tabs';
import { SearchInput } from '@/components/ui/search-input';
import { formatDate } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function AssignedTag() {
  return (
    <span className="ml-auto shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
      Assigned
    </span>
  );
}

interface Props {
  sopId: string;
  sopTitle: string;
  onClose: () => void;
}

export function AssignDialog({ sopId, sopTitle, onClose }: Props) {
  const queryClient = useQueryClient();
  const [pickTab, setPickTab] = useState<'user' | 'department'>('user');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedDepts, setSelectedDepts] = useState<Set<string>>(new Set());
  const [mandatory, setMandatory] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [search, setSearch] = useState('');

  // The whole active roster, not one 100-row page — search has to be able to find everyone.
  const usersQuery = useQuery({
    queryKey: ['users', 'all-active-for-sop'],
    queryFn: () => usersService.listAllActive(),
  });
  const deptsQuery = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });
  const assignmentsQuery = useQuery({
    queryKey: ['sop', 'assignments', sopId],
    queryFn: () => sopService.listAssignments(sopId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sop', 'assignments', sopId] });
    void queryClient.invalidateQueries({ queryKey: ['sop', 'sops'] });
    void queryClient.invalidateQueries({ queryKey: ['sop', 'stats'] });
  };

  const assign = useMutation({
    mutationFn: () => {
      const targets: SopAssignmentTarget[] = [
        ...[...selectedUsers].map((id) => ({ target_type: 'user' as const, target_id: id })),
        ...[...selectedDepts].map((id) => ({ target_type: 'department' as const, target_id: id })),
      ];
      return sopService.createAssignments(sopId, {
        targets,
        is_mandatory: mandatory,
        due_date: dueDate || null,
      });
    },
    onSuccess: (r) => {
      toast.success(`Assigned (${r.created})`);
      setSelectedUsers(new Set());
      setSelectedDepts(new Set());
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: (assignmentId: string) => sopService.deleteAssignment(assignmentId),
    onSuccess: () => { toast.success('Assignment removed'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const totalSelected = selectedUsers.size + selectedDepts.size;

  // One box filters whichever tab is showing; ticks outside the filter stay selected.
  const q = search.trim().toLowerCase();
  const filteredUsers = useMemo(
    () => (usersQuery.data ?? []).filter((u) => `${u.first_name} ${u.last_name}`.toLowerCase().includes(q)),
    [usersQuery.data, q],
  );
  const filteredDepts = useMemo(
    () => (deptsQuery.data?.data ?? []).filter((d) => d.name.toLowerCase().includes(q)),
    [deptsQuery.data, q],
  );

  // Already-assigned targets show ticked and locked; removing them is the trash icon below.
  const { assignedUsers, assignedDepts } = useMemo(() => {
    const users = new Set<string>();
    const depts = new Set<string>();
    for (const a of assignmentsQuery.data ?? []) {
      (a.target_type === 'department' ? depts : users).add(a.target_id);
    }
    return { assignedUsers: users, assignedDepts: depts };
  }, [assignmentsQuery.data]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign: {sopTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs
            tabs={[{ key: 'user', label: 'Users' }, { key: 'department', label: 'Departments' }]}
            active={pickTab}
            onChange={(k) => setPickTab(k as 'user' | 'department')}
          />

          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={pickTab === 'user' ? 'Search users…' : 'Search departments…'}
            containerClassName="w-full"
          />

          <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-border/60 p-2">
            {pickTab === 'user' ? (
              usersQuery.isLoading ? (
                <p className="p-2 text-sm text-muted-foreground">Loading…</p>
              ) : filteredUsers.length === 0 ? (
                <p className="p-2 text-sm text-muted-foreground">{q ? 'No users match your search' : 'No users found'}</p>
              ) : (
                filteredUsers.map((u) => {
                  const assigned = assignedUsers.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-sm',
                        assigned ? 'cursor-default' : 'cursor-pointer hover:bg-muted/40',
                      )}
                    >
                      <Checkbox
                        className="accent-primary disabled:opacity-80"
                        checked={assigned || selectedUsers.has(u.id)}
                        disabled={assigned}
                        onChange={() => toggle(selectedUsers, setSelectedUsers, u.id)}
                      />
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{u.first_name} {u.last_name}</span>
                      {assigned && <AssignedTag />}
                    </label>
                  );
                })
              )
            ) : (
              deptsQuery.isLoading ? (
                <p className="p-2 text-sm text-muted-foreground">Loading…</p>
              ) : filteredDepts.length === 0 ? (
                <p className="p-2 text-sm text-muted-foreground">{q ? 'No departments match your search' : 'No departments found'}</p>
              ) : (
                filteredDepts.map((d) => {
                  const assigned = assignedDepts.has(d.id);
                  return (
                    <label
                      key={d.id}
                      className={cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-sm',
                        assigned ? 'cursor-default' : 'cursor-pointer hover:bg-muted/40',
                      )}
                    >
                      <Checkbox
                        className="accent-primary disabled:opacity-80"
                        checked={assigned || selectedDepts.has(d.id)}
                        disabled={assigned}
                        onChange={() => toggle(selectedDepts, setSelectedDepts, d.id)}
                      />
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{d.name}</span>
                      {assigned && <AssignedTag />}
                    </label>
                  );
                })
              )
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <label className="flex h-10 cursor-pointer items-center gap-2 text-sm font-medium">
              <Checkbox checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
              Mandatory
            </label>
            <div className="flex items-center gap-2">
              <Label htmlFor="sop-assign-due-date" className="shrink-0">Due date</Label>
              <DatePicker id="sop-assign-due-date" value={dueDate} onChange={setDueDate} className="w-44" />
            </div>
          </div>

          <Button className="w-full" disabled={totalSelected === 0 || assign.isPending} onClick={() => assign.mutate()}>
            {assign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : `Assign SOP${totalSelected ? ` (${totalSelected})` : ''}`}
          </Button>

          <div className="border-t border-border/60 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current Assignments</p>
            {assignmentsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (assignmentsQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No assignments yet</p>
            ) : (
              <div className="space-y-1.5">
                {(assignmentsQuery.data ?? []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {a.target_type === 'department' ? <Users className="h-3.5 w-3.5 text-muted-foreground" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="truncate">{a.target_name ?? a.target_id}</span>
                      {a.target_type === 'department' && a.member_count != null && (
                        <span className="text-xs text-muted-foreground">({a.member_count})</span>
                      )}
                      {a.is_mandatory && <Badge variant="warning">Mandatory</Badge>}
                      {a.due_date && <span className="text-xs text-muted-foreground">Due {formatDate(a.due_date)}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => remove.mutate(a.id)}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Remove assignment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
